import { Device } from "@/hooks/useDevices";
import StatusBadge from "../StatusBadge";
import { getDeviceStatus } from "@/utils/deviceStatus";
import DeviceActions from "./DeviceActions";
import { Calendar, Activity, Heart } from "lucide-react";
import { AppleDeviceIcon } from "@/components/icons/AppleDeviceIcon";
import { GlassCard, GlassCardContent } from "../ui/glass-card";
import { calculateDeviceHealthScore, getHealthScoreColor } from "@/utils/deviceHealth";
import { useMemo } from "react";

interface DeviceHeaderProps {
  device: Device;
  onCardClick?: (tab: string) => void;
}

const DeviceHeader = ({ device, onCardClick }: DeviceHeaderProps) => {
  const status = getDeviceStatus(device.last_seen || device.created_at, device.status);
  const healthScore = useMemo(() => calculateDeviceHealthScore(device), [device]);
  
  // Script deployments count - from device data if available
  const scriptDeploymentsCount = device.script_deployments?.length || 0;

  const issuesCount = healthScore.metrics.filter(
    m => m.status === 'poor' || m.status === 'fair'
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppleDeviceIcon osType={device.os_type || 'macOS'} modelName={device.name} className="h-8 w-8 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">{device.name || device.hostname}</h1>
              <StatusBadge status={status} />
            </div>
            {device.name !== device.hostname && (
              <p className="text-sm text-muted-foreground">{device.hostname}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Actions</span>
          <DeviceActions />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard variant="subtle" hover={false} className="cursor-pointer group" onClick={() => onCardClick?.('info')}>
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Device Status</p>
                <h3 className="text-2xl font-bold text-foreground">{status.charAt(0).toUpperCase() + status.slice(1)}</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Last seen: {new Date(device.last_seen || device.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Activity className="h-5 w-5 text-primary" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        
        <GlassCard variant="subtle" hover={false} className="cursor-pointer group" onClick={() => onCardClick?.('deployments')}>
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Scheduled Actions</p>
                <h3 className="text-2xl font-bold text-foreground">{scriptDeploymentsCount}</h3>
                <p className="text-sm text-muted-foreground mt-2">Pending deployments</p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-accent border border-accent/20 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-accent-foreground" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        
        <GlassCard variant="subtle" hover={false} className="cursor-pointer group" onClick={() => onCardClick?.('analytics')}>
          <GlassCardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Health Score</p>
                <div className="flex items-baseline gap-2">
                  <h3 className="text-2xl font-bold text-foreground">{healthScore.overall}</h3>
                  <span className="text-sm font-semibold px-2 py-0.5 rounded" style={{ 
                    backgroundColor: getHealthScoreColor(healthScore.overall) + '20',
                    color: getHealthScoreColor(healthScore.overall)
                  }}>
                    Grade {healthScore.grade}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {issuesCount > 0 ? `${issuesCount} ${issuesCount === 1 ? 'issue' : 'issues'} need attention` : 'All systems healthy'}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ 
                backgroundColor: `${getHealthScoreColor(healthScore.overall)}1A`,
                borderColor: `${getHealthScoreColor(healthScore.overall)}33`
              }}>
                <Heart className="h-5 w-5" style={{ color: getHealthScoreColor(healthScore.overall) }} />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </div>
  );
};

export default DeviceHeader;
